import { MAX_FLOW_ELEMENT_IDS } from "@redotech/customer-portal-sdk/rpc/schema/get-flow-element-text-overlay";
import { DEFAULT_LOCALE, useTranslation } from "@redotech/redo-i18n";
import { ReturnFlow } from "@redotech/redo-return-core-common/return-flow";
import { flowElementIdsOfSteps } from "@redotech/redo-return-core-common/return-flow/flow-element-id-spec";
import {
  FlowElementTextOverlay,
  FlowElementTranslatableField,
} from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import {
  type LocalizationSettings,
  localizationEnabled,
} from "@redotech/redo-team-common/localization/settings";
import { useQuery } from "@tanstack/react-query";
import { createContext, useCallback, useContext } from "react";
import { RpcClientContext } from "./contexts/rpc";
import { localizeCustomerPortalFlowText } from "./util";

/** The empty default falls every lookup through to the built-in localization. */
export const FlowElementTextOverlayContext =
  createContext<FlowElementTextOverlay>({});

const EMPTY_FLOW_ELEMENT_TEXT_OVERLAY: FlowElementTextOverlay = {};

export function useFlowElementTextOverlay({
  elementIds,
  localization,
}: {
  elementIds: readonly string[];
  localization: LocalizationSettings | null | undefined;
}): FlowElementTextOverlay {
  const client = useContext(RpcClientContext);
  const { i18n } = useTranslation();
  const language = i18n.language.split("-")[0] ?? i18n.language;
  const requestedElementIds = [...new Set(elementIds)].slice(
    0,
    MAX_FLOW_ELEMENT_IDS,
  );
  const elementIdsKey = requestedElementIds.join("\n");
  const enabled =
    !!client &&
    localizationEnabled(localization) &&
    language !== DEFAULT_LOCALE &&
    requestedElementIds.length > 0;
  const query = useQuery({
    queryKey: ["flowElementTextOverlay", language, elementIdsKey],
    queryFn: async () => {
      if (!client) {
        return EMPTY_FLOW_ELEMENT_TEXT_OVERLAY;
      }
      const { overlay } = await client.getFlowElementTextOverlay({
        elementIds: requestedElementIds,
        language,
      });
      return overlay;
    },
    enabled,
    staleTime: Infinity,
  });

  return enabled
    ? (query.data ?? EMPTY_FLOW_ELEMENT_TEXT_OVERLAY)
    : EMPTY_FLOW_ELEMENT_TEXT_OVERLAY;
}

/**
 * Display-only, per the invariant on FlowElementTextOverlay: the merchant's
 * translation, else the Redo-default localization, else the English text.
 */
export function flowElementDisplayText(
  overlay: FlowElementTextOverlay,
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  text: string,
): string {
  const translated = elementId ? overlay[elementId]?.[field] : undefined;
  return translated || localizeCustomerPortalFlowText(text);
}

/**
 * Overlay first. Merchant custom copy stays canonical. Redo defaults keep t().
 */
export function flowElementCustomOrDefaultText(
  overlay: FlowElementTextOverlay,
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  custom: string | undefined,
  defaultEnglish: string,
  defaultTranslated: string,
): string {
  const hasCustom = !!custom && custom !== defaultEnglish;
  const canonical = hasCustom ? custom : defaultEnglish;
  const displayed = flowElementDisplayText(
    overlay,
    elementId,
    field,
    canonical,
  );
  if (displayed !== canonical) {
    return displayed;
  }
  return hasCustom ? canonical : defaultTranslated;
}

export function collectFlowElementIds(
  flows: (ReturnFlow | undefined)[],
): string[] {
  return flowElementIdsOfSteps(flows.flatMap((flow) => flow?.steps ?? []));
}

export function useFlowElementText(): (
  elementId: string | null | undefined,
  field: FlowElementTranslatableField,
  text: string,
) => string {
  const overlay = useContext(FlowElementTextOverlayContext);
  return useCallback(
    (elementId, field, text) =>
      flowElementDisplayText(overlay, elementId, field, text),
    [overlay],
  );
}
